import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Modal,
  LinearProgress,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

const allowedFileTypes = [
  "application/msword", // .doc
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx
  "application/pdf", // .pdf
  "image/jpeg", // .jpg
  "image/png", // .png
  "application/vnd.ms-excel", // .xls
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
  "text/plain",
];

const allowedExtensions = [
  "doc",
  "docx",
  "pdf",
  "jpg",
  "jpeg",
  "png",
  "xls",
  "xlsx",
  "txt",
];

const UploadModal = ({
  open,
  onClose,
  handleFileUploadClick,
  fileInputRef,
  handleFileChange,
  uploadProgress,
  confirmUpload,
}: any) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  const validateFile = (file: any) => {
    const fileExtension = file.name.split(".").pop().toLowerCase();

    if (
      !allowedFileTypes.includes(file.type) ||
      !allowedExtensions.includes(fileExtension)
    ) {
      setErrorMessage(
        "This file type is not supported. Please upload a valid file."
      );
      return false;
    }
    return true;
  };

  const handleFileSelection = (event: any) => {
    setErrorMessage("");
    if (event.target.files.length > 0) {
      const file = event.target.files[0];

      if (!validateFile(file)) {
        return;
      }

      setSelectedFile(file);
      handleFileChange(event);
    }
  };

  // Prevent default drag behavior
  const handleDragOver = (event: any) => {
    event.preventDefault();
    event.stopPropagation();
  };

  // Handle file drop
  const handleDrop = (event: any) => {
    event.preventDefault();
    event.stopPropagation();
    setErrorMessage("");

    if (event.dataTransfer.files.length > 0) {
      const file = event.dataTransfer.files[0];

      if (!validateFile(file)) {
        return;
      }

      const syntheticEvent = {
        target: { files: event.dataTransfer.files },
      };
      setSelectedFile(file);
      handleFileChange(syntheticEvent);
    }
  };

  // Handle modal close and reset state
  const handleClose = () => {
    setSelectedFile(null); // Reset file selection
    setErrorMessage(""); // Clear any previous error message
    if (fileInputRef.current) {
      fileInputRef.current.value = ""; // Reset file input field
    }
    onClose(); // Call parent onClose function
  };

  useEffect(() => {
    if (!open) {
      setSelectedFile(null);
      setErrorMessage("");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  }, [open]);

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "30rem",
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4,
          borderRadius: "8px",
        }}
      >
        <IconButton
          onClick={handleClose}
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            p: 1,
          }}
        >
          <CloseIcon />
        </IconButton>

        {/* Header */}
        <Box
          sx={{
            textAlign: "center",
            bgcolor: "#d9f7be",
            py: 1,
            borderRadius: "4px",
            mb: 2,
          }}
        >
          <Typography variant="h6" component="div">
            Upload
          </Typography>
        </Box>

        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        )}

        {selectedFile ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
            <Typography variant="body1">{selectedFile?.name}</Typography>
          </Box>
        ) : (
          <Box
            sx={{
              border: "2px dashed #ccc",
              borderRadius: "8px",
              textAlign: "center",
              py: 6,
              px: 2,
              mb: 3,
              cursor: "pointer",
            }}
            onClick={handleFileUploadClick}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            <CloudUploadIcon sx={{ fontSize: 40, color: "#666" }} />
            <Typography variant="body1" sx={{ mt: 1 }}>
              Upload file <span style={{ color: "red" }}>*</span>
            </Typography>
            <Typography variant="body2" sx={{ color: "gray", mt: 1 }}>
              Click to{" "}
              <span style={{ color: "blue", cursor: "pointer" }}>browse</span>{" "}
              or <br />
              drag and drop your file
            </Typography>
            <Typography variant="body2" sx={{ color: "gray", mt: 1 }}>
              Supported file types: .txt, .doc, .docx, .pdf, .xls, .xlsx, images
            </Typography>
            <Typography variant="body2" sx={{ color: "gray", mt: 1 }}>
              Maximum file size: 40 MB
            </Typography>
            <Typography variant="body2" sx={{ color: "red", mt: 1 }}>
              Disclaimer: Optimized for file sizes of upto 20MB and 1000 pages
            </Typography>
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: "none" }}
              accept=".txt, .doc, .docx, .pdf, .xls, .xlsx, image/*"
              onChange={handleFileSelection}
            />
          </Box>
        )}

        {selectedFile && uploadProgress > 0 && (
          <Box sx={{ width: "100%", my: 2, borderRadius: 3 }}>
            <LinearProgress variant="determinate" value={uploadProgress} />
          </Box>
        )}

        {/* Buttons */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <Button
            variant="contained"
            color="primary"
            sx={{ bgcolor: "#6495ED" }}
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={confirmUpload}
            disabled={!selectedFile}
          >
            Confirm
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default UploadModal;
